import React, { useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { GripVertical, Plus } from 'lucide-react';
import {
  getDocIntelligenceAgreementBlocks,
  getDocIntelligenceMappingRegistry,
  getDynamicFields
} from '../../services/api/implementation/impl';
import { humanizeFieldName } from './docIntelUtils';

const EDITOR_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
    ['clean']
  ]
};

function FieldChip({ label, placeholder, onInsert, disabled }) {
  return (
    <button
      type="button"
      disabled={disabled}
      draggable={!disabled}
      onDragStart={(e) => {
        e.dataTransfer.setData('text/plain', `{{ ${placeholder} }}`);
        e.dataTransfer.effectAllowed = 'copy';
      }}
      onClick={() => onInsert(placeholder)}
      className="group inline-flex max-w-full items-center gap-1 rounded-lg border border-teal-200/80 bg-teal-50/80 px-2 py-1 text-left text-[11px] text-teal-950 transition hover:border-teal-300 hover:bg-teal-100 disabled:cursor-not-allowed disabled:opacity-50">
      <GripVertical className="h-3 w-3 shrink-0 text-teal-700/70 opacity-60 group-hover:opacity-100" />
      <span className="truncate font-medium">{label}</span>
      <Plus className="ml-auto h-3 w-3 shrink-0 text-teal-800/70" />
    </button>
  );
}

const DocIntelRichTemplateEditor = ({
  value,
  onChange,
  disabled = false,
  suggestedFields = [],
  className = '',
  hideFieldPalette = false
}) => {
  const quillRef = useRef(null);

  const registryQuery = useQuery({
    queryKey: ['docIntelligenceRegistry'],
    queryFn: getDocIntelligenceMappingRegistry,
    staleTime: 5 * 60 * 1000
  });

  const agreementBlocksQuery = useQuery({
    queryKey: ['docIntelligenceAgreementBlocks'],
    queryFn: getDocIntelligenceAgreementBlocks,
    staleTime: 10 * 60 * 1000
  });

  const dynamicFieldsQuery = useQuery({
    queryKey: ['dynamic-fields', 'doc-intel-editor'],
    queryFn: getDynamicFields,
    staleTime: 10 * 60 * 1000
  });

  const insertPlaceholder = (placeholder) => {
    if (disabled) return;
    const token = `{{ ${placeholder} }}`;
    const editor = quillRef.current?.getEditor?.();
    if (!editor) {
      onChange(`${value || ''}${value ? ' ' : ''}${token}`);
      return;
    }
    const range = editor.getSelection(true);
    const index = range ? range.index : editor.getLength();
    editor.insertText(index, token);
    editor.setSelection(index + token.length);
    onChange(editor.root.innerHTML);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (disabled) return;
    const token = e.dataTransfer.getData('text/plain');
    if (!token.includes('{{')) return;
    const editor = quillRef.current?.getEditor?.();
    if (!editor) return;
    const range = editor.getSelection(true);
    const index = range ? range.index : editor.getLength();
    editor.insertText(index, token);
    editor.setSelection(index + token.length);
    onChange(editor.root.innerHTML);
  };

  const detectedChips = useMemo(() => {
    const seen = new Set();
    return (suggestedFields || [])
      .filter((field) => {
        const key = field.placeholder || field.name || field;
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .map((field) => {
        if (typeof field === 'string') {
          return { placeholder: field, label: humanizeFieldName(field) };
        }
        return {
          placeholder: field.placeholder || field.name,
          label: field.label || humanizeFieldName(field.placeholder || field.name)
        };
      });
  }, [suggestedFields]);

  const registryChips = useMemo(() => {
    const rules = registryQuery.data?.mapping_rules ?? [];
    const seen = new Set(detectedChips.map((chip) => chip.placeholder));
    return rules
      .filter((rule) => rule.placeholder_name && !seen.has(rule.placeholder_name))
      .map((rule) => ({
        placeholder: rule.placeholder_name,
        label: humanizeFieldName(rule.placeholder_name)
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [registryQuery.data, detectedChips]);

  const dynamicChips = useMemo(() => {
    const list = dynamicFieldsQuery.data;
    if (!Array.isArray(list)) return [];
    const seen = new Set([
      ...detectedChips.map((c) => c.placeholder),
      ...registryChips.map((c) => c.placeholder)
    ]);
    return list
      .filter((field) => field.column_name && !seen.has(field.column_name))
      .slice(0, 24)
      .map((field) => ({
        placeholder: String(field.column_name).toLowerCase(),
        label: field.readable_name || field.column_name
      }))
      .sort((a, b) => String(a.label).localeCompare(String(b.label)));
  }, [dynamicFieldsQuery.data, detectedChips, registryChips]);

  /** Per-block expected_entities from agreement_block_schema — primary Insert palette. */
  const blockPaletteGroups = useMemo(() => {
    const blocks =
      agreementBlocksQuery.data?.blocks ||
      registryQuery.data?.agreement_blocks?.blocks ||
      [];
    return blocks
      .filter((block) => Array.isArray(block.expected_entities) && block.expected_entities.length)
      .map((block) => ({
        id: block.schema_id || block.id,
        title: block.name,
        reuseType: block.reuse_type,
        chips: block.expected_entities.map((entity) => ({
          placeholder: entity.key,
          label: humanizeFieldName(entity.key),
          type: entity.type
        }))
      }));
  }, [agreementBlocksQuery.data, registryQuery.data]);

  const renderChipGroup = (title, chips, subtitle) => {
    if (!chips.length) return null;
    return (
      <div>
        <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
          {title}
        </p>
        {subtitle ? (
          <p className="mb-1.5 text-[10px] leading-snug text-slate-400">{subtitle}</p>
        ) : null}
        <div className="flex flex-wrap gap-1.5">
          {chips.map((chip) => (
            <FieldChip
              key={chip.placeholder}
              label={chip.label}
              placeholder={chip.placeholder}
              onInsert={insertPlaceholder}
              disabled={disabled}
            />
          ))}
        </div>
      </div>
    );
  };

  return (
    <div
      className={`grid gap-3 ${hideFieldPalette ? '' : 'lg:grid-cols-[minmax(0,1fr)_240px]'} ${className}`}>
      <div
        className="doc-intel-quill rounded-xl border border-slate-200 bg-white [&_.ql-container]:min-h-[280px] [&_.ql-editor]:min-h-[260px]"
        onDragOver={(e) => e.preventDefault()}
        onDrop={handleDrop}>
        <ReactQuill
          ref={quillRef}
          theme="snow"
          value={value || ''}
          onChange={onChange}
          readOnly={disabled}
          modules={EDITOR_MODULES}
          placeholder="Edit agreement body — click or drag fields from the panel to insert placeholders…"
          className="bg-white"
        />
      </div>

      {!hideFieldPalette && (
        <aside className="max-h-[420px] space-y-3 overflow-y-auto rounded-xl border border-slate-200 bg-slate-50/60 p-3">
          <p className="text-xs font-medium text-slate-700">Insert fields</p>
          <p className="text-[11px] leading-relaxed text-slate-500">
            Grouped by agreement block — click or drag to insert{' '}
            <code>{`{{ placeholder }}`}</code>.
          </p>
          {renderChipGroup('Detected from upload', detectedChips)}
          {blockPaletteGroups.map((group) =>
            renderChipGroup(
              group.title,
              group.chips,
              group.reuseType ? `reuse: ${group.reuseType}` : undefined
            )
          )}
          {renderChipGroup('Common agreement fields', registryChips)}
          {renderChipGroup('Dynamic fields', dynamicChips)}
        </aside>
      )}
    </div>
  );
};

export default DocIntelRichTemplateEditor;
